import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TableSortLabel,
  IconButton,
  Collapse,
  Box,
  Typography,
  Dialog,
  TextField,
  Button,
  Alert,
  Chip,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Label,
  BarChart,
  Bar,
  ComposedChart,
} from "recharts";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import LaunchIcon from "@mui/icons-material/Launch";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import ChatIcon from "@mui/icons-material/Chat";
import TF from "../api/requests/tf_backend";

function Row({ row }) {
  const [open, setOpen] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [stockDetails, setStockDetails] = useState(null);
  const [mainOrder, setMainOrder] = useState({
    quantity: "",
    amount: "",
  });
  const [gttOrders, setGttOrders] = useState([
    { price: row.last_price || "", amount: "", quantity: "" },
  ]);
  const [comments, setComments] = useState(row.comments || []);
  const [newComment, setNewComment] = useState({
    comment: "",
    category: "Technical",
    sentiment: "Neutral",
  });
  const [activeTab, setActiveTab] = useState(0);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatPrice = (price) => {
    return parseFloat(price).toFixed(2);
  };

  const formatPercentage = (percentage) => {
    return parseFloat(percentage).toFixed(2) + "%";
  };

  const calculatePercentageChange = (currentPrice, previousPrice) => {
    return ((currentPrice - previousPrice) / previousPrice) * 100;
  };

  const fetchStockDetails = async (symbol) => {
    console.log("Fetching stock details for symbol:", symbol);
    try {
      const stockData = await TF.fetchStockDetails({ symbol, duration: 30 });
      console.log("Stock Details Response:", stockData);
      return {
        ...stockData?.quoteInfo?.[`NSE:${symbol}`],
        percentageChange: stockData.percentageChange,
        yesterdayClose: stockData.yesterdayClose,
        ltp: stockData.ltp,
        comments: stockData.comments || [],
        historicData: stockData.historicData || [],
      };
    } catch (error) {
      console.error("Error fetching stock details:", error);
      return null;
    }
  };

  const handleMainOrderChange = (field, value) => {
    const numValue = value === "" ? "" : parseFloat(value);
    const triggerPrice = parseFloat(row.trigger_price || 0);

    setMainOrder((prev) => {
      const newDetails = { ...prev };
      if (field === "quantity") {
        newDetails.quantity = numValue;
        newDetails.amount = numValue * triggerPrice;
      } else {
        newDetails.amount = numValue;
        newDetails.quantity = Math.floor(numValue / triggerPrice);
      }
      return newDetails;
    });
  };

  const handleGttChange = (index, field, value) => {
    console.log("GTT Change:", { index, field, value });
    setGttOrders((prev) => {
      const newOrders = [...prev];
      const order = { ...newOrders[index] };

      if (field === "quantity") {
        order.quantity = value;
        if (value && order.price) {
          order.amount = (parseFloat(value) * parseFloat(order.price)).toFixed(
            2
          );
        } else {
          order.amount = "";
        }
      } else if (field === "amount") {
        order.amount = value;
        if (value && order.price) {
          order.quantity = Math.floor(
            parseFloat(value) / parseFloat(order.price)
          );
        } else {
          order.quantity = "";
        }
      } else {
        order.price = value;
        if (order.quantity && value) {
          order.amount = (
            parseFloat(order.quantity) * parseFloat(value)
          ).toFixed(2);
        } else {
          order.amount = "";
        }
      }

      console.log("Updated Order:", order);
      newOrders[index] = order;
      return newOrders;
    });
  };

  const handlePlaceOrder = async () => {
    try {
      await TF.placeOrder({
        tradingsymbol: row.trading_symbol,
        exchange: "NSE",
        transaction_type: "BUY",
        quantity: parseInt(mainOrder.quantity),
        order_type: "MARKET",
        product: "CNC",
        variety: "regular",
      });

      setOrderSuccess({
        type: "success",
        message: "Order placed successfully!",
      });
    } catch (error) {
      setOrderSuccess({
        type: "error",
        message: error?.response?.data || "Failed to place order.",
      });
    }
  };

  const handlePlaceGttOrders = async () => {
    try {
      const promises = gttOrders.map((order) =>
        TF.placeOrder({
          tradingsymbol: row.trading_symbol,
          exchange: "NSE",
          transaction_type: "BUY",
          quantity: parseInt(order.quantity),
          order_type: "LIMIT",
          price: parseFloat(order.price),
          product: "CNC",
          variety: "regular",
        })
      );

      await Promise.all(promises);
      setOrderSuccess({
        type: "success",
        message: "LIMIT orders placed successfully!",
      });
    } catch (error) {
      setOrderSuccess({
        type: "error",
        message: error?.response?.data || "Failed to place LIMIT orders.",
      });
    }
  };

  const handleScreenerRedirect = (e, symbol) => {
    e.stopPropagation();
    window.open(`https://www.screener.in/company/${symbol}`, "_blank");
  };

  const handleTradingViewRedirect = (e, symbol) => {
    e.stopPropagation();
    window.open(
      `https://www.tradingview.com/chart?symbol=NSE:${symbol}`,
      "_blank"
    );
  };

  const handleAddComment = async () => {
    try {
      await TF.addComment({
        trading_symbol: row.trading_symbol,
        ...newComment,
      });
      const response = await TF.getSmartAlerts({
        limit: 10,
        offset: 0,
        sort_by: "last_occurrence",
        sort_order: "desc",
      });
      const updatedRow = response.data.find(
        (item) => item.trading_symbol === row.trading_symbol
      );
      if (updatedRow) {
        setComments(updatedRow.comments || []);
      }
      setNewComment({
        comment: "",
        category: "Technical",
        sentiment: "Neutral",
      });
    } catch (error) {
      console.error("Error adding comment:", error);
    }
  };

  useEffect(() => {
    if (open) {
      console.log("Row opened, fetching details for:", row.trading_symbol);
      const fetchDetails = async () => {
        setDialogLoading(true);
        try {
          const details = await fetchStockDetails(row.trading_symbol);
          console.log("Fetched Details:", details);
          setStockDetails(details);
          setGttOrders([
            { price: row.last_price || "", amount: "", quantity: "" },
          ]);
        } catch (error) {
          console.error("Error fetching details:", error);
        } finally {
          setDialogLoading(false);
        }
      };
      fetchDetails();
    }
  }, [open, row.trading_symbol]);

  const timelineData = useMemo(() => {
    // Group alerts by month
    const monthlyAlerts = row.timeline.reduce((acc, point) => {
      const date = new Date(point.triggered_at);
      const monthKey = `${date.getFullYear()}-${date.getMonth()}`;

      if (!acc[monthKey]) {
        acc[monthKey] = [];
      }
      acc[monthKey].push({
        date: date.getTime(),
        price: parseFloat(point.trigger_price),
        triggered_at: point.triggered_at,
      });
      return acc;
    }, {});

    // Create connected points for each month
    const alertData = Object.values(monthlyAlerts).flatMap((monthPoints) => {
      const sortedPoints = monthPoints.sort((a, b) => a.date - b.date);
      const connectedPoints = [];
      for (let i = 0; i < sortedPoints.length; i++) {
        connectedPoints.push(sortedPoints[i]);
        if (i < sortedPoints.length - 1) {
          const midDate = (sortedPoints[i].date + sortedPoints[i + 1].date) / 2;
          const midPrice =
            (sortedPoints[i].price + sortedPoints[i + 1].price) / 2;
          connectedPoints.push({
            date: midDate,
            price: midPrice,
            isConnecting: true,
          });
        }
      }
      return connectedPoints;
    });

    // Format historic data for candlestick chart
    const historicData =
      stockDetails?.historicData?.map((point) => ({
        date: new Date(point.date).getTime(),
        open: parseFloat(point.open),
        high: parseFloat(point.high),
        low: parseFloat(point.low),
        close: parseFloat(point.close),
        volume: parseInt(point.volume),
      })) || [];

    console.log("Historic Data:", historicData);

    return {
      alertData: alertData.sort((a, b) => a.date - b.date),
      historicData: historicData.sort((a, b) => a.date - b.date),
    };
  }, [row.timeline, stockDetails?.historicData]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: "white",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "4px",
          }}
        >
          <Typography variant="body2">
            Date: {new Date(label).toLocaleDateString()}
          </Typography>
          {payload.map((entry, index) => (
            <Typography key={index} variant="body2" color={entry.color}>
              {entry.name}: ₹{entry.value.toFixed(2)}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  const renderCommentsSection = () => (
    <Box sx={{ mb: 3 }}>
      {/* Add Comment Form */}
      <Box sx={{ mb: 2, p: 1, bgcolor: "background.paper", borderRadius: 1 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Add comment..."
            value={newComment.comment}
            onChange={(e) =>
              setNewComment((prev) => ({ ...prev, comment: e.target.value }))
            }
            sx={{ flex: 1 }}
          />
          <FormControl size="small" sx={{ width: 100 }}>
            <Select
              value={newComment.category}
              size="small"
              onChange={(e) =>
                setNewComment((prev) => ({ ...prev, category: e.target.value }))
              }
            >
              <MenuItem value="Technical">Technical</MenuItem>
              <MenuItem value="Fundamental">Fundamental</MenuItem>
              <MenuItem value="News">News</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ width: 100 }}>
            <Select
              value={newComment.sentiment}
              size="small"
              onChange={(e) =>
                setNewComment((prev) => ({
                  ...prev,
                  sentiment: e.target.value,
                }))
              }
            >
              <MenuItem value="Bullish">Bullish</MenuItem>
              <MenuItem value="Bearish">Bearish</MenuItem>
              <MenuItem value="Neutral">Neutral</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            size="small"
            onClick={handleAddComment}
            disabled={!newComment.comment}
            startIcon={<AddIcon />}
          >
            Add
          </Button>
        </Box>
      </Box>

      {/* Comments List */}
      {comments.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 1, color: "text.secondary" }}>
          <Typography variant="body2">No comments yet</Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {comments.map((comment, index) => (
            <Box
              key={comment.id || index}
              sx={{
                p: 1,
                bgcolor: "background.paper",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {comment.comment}
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                  <Chip
                    size="small"
                    label={comment.category}
                    sx={{ height: 20, fontSize: "0.65rem" }}
                  />
                  <Chip
                    size="small"
                    label={comment.sentiment}
                    color={
                      comment.sentiment === "Bullish"
                        ? "success"
                        : comment.sentiment === "Bearish"
                        ? "error"
                        : "default"
                    }
                    icon={
                      comment.sentiment === "Bullish" ? (
                        <TrendingUpIcon sx={{ fontSize: "0.75rem" }} />
                      ) : comment.sentiment === "Bearish" ? (
                        <TrendingDownIcon sx={{ fontSize: "0.75rem" }} />
                      ) : null
                    }
                    sx={{ height: 20, fontSize: "0.65rem" }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {new Date(comment.created_at).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );

  const renderOrderSection = () => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Place Orders
      </Typography>
      {/* Market Order */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ mb: 1, color: "#666", fontSize: "0.875rem" }}>
          Market Order
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            size="small"
            placeholder="Amount"
            value={mainOrder.amount || row.last_price}
            onChange={(e) => handleMainOrderChange("amount", e.target.value)}
            sx={{ flex: 1 }}
            InputProps={{ sx: { fontSize: "0.875rem" } }}
          />
          <TextField
            size="small"
            placeholder="Quantity"
            value={mainOrder.quantity}
            onChange={(e) => handleMainOrderChange("quantity", e.target.value)}
            sx={{ flex: 1 }}
            InputProps={{ sx: { fontSize: "0.875rem" } }}
          />
        </Box>
        <Button
          variant="contained"
          fullWidth
          onClick={handlePlaceOrder}
          sx={{ mt: 1, fontSize: "0.875rem" }}
        >
          Place Market Order
        </Button>
      </Box>

      {/* LIMIT Orders */}
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography sx={{ color: "#666", fontSize: "0.875rem" }}>
            LIMIT Orders
          </Typography>
          <IconButton
            onClick={() => {
              console.log("Adding new GTT order");
              setGttOrders((prev) => [
                ...prev,
                {
                  price: row.last_price || "",
                  amount: "",
                  quantity: "",
                },
              ]);
            }}
            size="small"
            sx={{ ml: 1 }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
        {gttOrders.map((order, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              <TextField
                size="small"
                placeholder="Price"
                value={order.price || ""}
                onChange={(e) =>
                  handleGttChange(index, "price", e.target.value)
                }
                sx={{ flex: 1 }}
                InputProps={{ sx: { fontSize: "0.875rem" } }}
              />
              <TextField
                size="small"
                placeholder="Quantity"
                value={order.quantity || ""}
                onChange={(e) =>
                  handleGttChange(index, "quantity", e.target.value)
                }
                sx={{ flex: 1 }}
                InputProps={{ sx: { fontSize: "0.875rem" } }}
              />
              <TextField
                size="small"
                placeholder="Amount"
                value={order.amount || ""}
                onChange={(e) =>
                  handleGttChange(index, "amount", e.target.value)
                }
                sx={{ flex: 1 }}
                InputProps={{ sx: { fontSize: "0.875rem" } }}
              />
              {gttOrders.length > 1 && (
                <IconButton
                  size="small"
                  onClick={() => {
                    console.log("Deleting GTT order at index:", index);
                    const newOrders = gttOrders.filter((_, i) => i !== index);
                    setGttOrders(newOrders);
                  }}
                  sx={{ color: "#d32f2f" }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        ))}

        {gttOrders.length > 0 && (
          <Button
            variant="outlined"
            fullWidth
            onClick={handlePlaceGttOrders}
            sx={{ fontSize: "0.875rem" }}
          >
            Place LIMIT Orders
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{row.trading_symbol}</TableCell>
        <TableCell>{row.trigger_count}</TableCell>
        <TableCell>{formatDate(row.first_occurrence)}</TableCell>
        <TableCell>{formatDate(row.last_occurrence)}</TableCell>
        <TableCell>{formatPrice(row.first_price)}</TableCell>
        <TableCell>{formatPrice(row.last_price)}</TableCell>
        <TableCell>{formatPercentage(row.price_increase_percentage)}</TableCell>
        <TableCell>{row.stockHolding?.quantity || 0}</TableCell>
        <TableCell>{row.stockHolding?.average_price || 0}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              {dialogLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                  <CircularProgress size={40} />
                </Box>
              ) : (
                <>
                  {orderSuccess && (
                    <Alert
                      severity={orderSuccess.type}
                      sx={{ mb: 2 }}
                      onClose={() => setOrderSuccess(null)}
                    >
                      {orderSuccess.message}
                    </Alert>
                  )}

                  <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                    <Tabs
                      value={activeTab}
                      onChange={(e, newValue) => setActiveTab(newValue)}
                      aria-label="stock details tabs"
                    >
                      <Tab label="Overview" />
                      <Tab label="Orders" />
                      <Tab label="Comments" />
                    </Tabs>
                  </Box>

                  {activeTab === 0 && (
                    <Box>
                      <Box sx={{ mb: 3 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mb: 2,
                          }}
                        >
                          <Typography
                            sx={{ fontSize: "1.125rem", fontWeight: 500 }}
                          >
                            {row.trading_symbol}
                          </Typography>
                          <Chip label="NSE" size="small" sx={{ height: 24 }} />
                          <Typography sx={{ fontSize: "0.875rem" }}>
                            LTP: ₹{formatPrice(row.last_price)}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={(e) =>
                                handleScreenerRedirect(e, row.trading_symbol)
                              }
                              sx={{
                                padding: "3px",
                                color: "#1976d2",
                                "& svg": { fontSize: "16px" },
                              }}
                            >
                              <LaunchIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={(e) =>
                                handleTradingViewRedirect(e, row.trading_symbol)
                              }
                              sx={{
                                padding: "3px",
                                color: "#1976d2",
                                "& svg": { fontSize: "16px" },
                              }}
                            >
                              <ShowChartIcon />
                            </IconButton>
                          </Box>
                          {row.price_increase_percentage && (
                            <Chip
                              icon={
                                row.price_increase_percentage >= 0 ? (
                                  <TrendingUpIcon />
                                ) : (
                                  <TrendingDownIcon />
                                )
                              }
                              label={`${formatPercentage(
                                row.price_increase_percentage
                              )}`}
                              color={
                                row.price_increase_percentage >= 0
                                  ? "success"
                                  : "error"
                              }
                              size="small"
                              sx={{ height: 24 }}
                            />
                          )}
                        </Box>

                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={6}>
                            <Typography
                              sx={{ fontSize: "0.875rem", color: "#666" }}
                            >
                              Previous Close
                            </Typography>
                            <Typography sx={{ fontSize: "0.875rem" }}>
                              ₹{formatPrice(row.first_price)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              sx={{ fontSize: "0.875rem", color: "#666" }}
                            >
                              Day's Range
                            </Typography>
                            <Typography sx={{ fontSize: "0.875rem" }}>
                              ₹{formatPrice(row.first_price)} - ₹
                              {formatPrice(row.last_price)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Alert Points Graph - Now First */}
                      <Typography variant="h6" gutterBottom component="div">
                        Alert Timeline
                      </Typography>
                      <Box sx={{ height: 300, mb: 3 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={timelineData.alertData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="date"
                              angle={-45}
                              textAnchor="end"
                              height={70}
                              scale="time"
                              type="number"
                              domain={["dataMin", "dataMax"]}
                              tickFormatter={(value) =>
                                new Date(value).toLocaleDateString()
                              }
                            />
                            <YAxis
                              domain={["auto", "auto"]}
                              tickFormatter={(value) => `₹${value}`}
                            />
                            <Tooltip
                              content={({ active, payload, label }) => {
                                if (active && payload && payload.length) {
                                  return (
                                    <Box
                                      sx={{
                                        backgroundColor: "white",
                                        padding: "10px",
                                        border: "1px solid #ccc",
                                        borderRadius: "4px",
                                      }}
                                    >
                                      <Typography variant="body2">
                                        Date:{" "}
                                        {new Date(label).toLocaleDateString()}
                                      </Typography>
                                      {payload.map((entry, index) => (
                                        <Typography
                                          key={index}
                                          variant="body2"
                                          color={entry.color}
                                        >
                                          {entry.name}: ₹
                                          {entry.value.toFixed(2)}
                                        </Typography>
                                      ))}
                                    </Box>
                                  );
                                }
                                return null;
                              }}
                            />
                            <Line
                              type="monotone"
                              dataKey="price"
                              stroke="#ff0000"
                              name="Alert Price"
                              dot={(props) => {
                                const { cx, cy, payload } = props;
                                if (payload.isConnecting) return null;
                                return (
                                  <circle
                                    cx={cx}
                                    cy={cy}
                                    r={4}
                                    fill="#ff0000"
                                    stroke="#fff"
                                    strokeWidth={2}
                                  />
                                );
                              }}
                              activeDot={{ r: 6 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>

                      {/* Historic Data Graph - Now Second */}
                      <Typography variant="h6" gutterBottom component="div">
                        Historic Price Data
                      </Typography>
                      <Box sx={{ height: 300, mb: 3 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart
                            data={
                              stockDetails?.historicData?.map((point) => ({
                                ...point,
                                date: new Date(point.date).getTime(),
                                open: parseFloat(point.open),
                                high: parseFloat(point.high),
                                low: parseFloat(point.low),
                                close: parseFloat(point.close),
                                volume: parseInt(point.volume),
                              })) || []
                            }
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="date"
                              angle={-45}
                              textAnchor="end"
                              height={70}
                              scale="time"
                              type="number"
                              domain={["dataMin", "dataMax"]}
                              tickFormatter={(value) =>
                                new Date(value).toLocaleDateString()
                              }
                            />
                            <YAxis
                              domain={["auto", "auto"]}
                              tickFormatter={(value) => `₹${value}`}
                            />
                            <Tooltip
                              content={({ active, payload, label }) => {
                                if (active && payload && payload.length) {
                                  const data = payload[0].payload;
                                  return (
                                    <Box
                                      sx={{
                                        backgroundColor: "white",
                                        padding: "10px",
                                        border: "1px solid #ccc",
                                        borderRadius: "4px",
                                      }}
                                    >
                                      <Typography variant="body2">
                                        Date:{" "}
                                        {new Date(label).toLocaleDateString()}
                                      </Typography>
                                      <Typography variant="body2">
                                        Open: ₹{data.open.toFixed(2)}
                                      </Typography>
                                      <Typography variant="body2">
                                        High: ₹{data.high.toFixed(2)}
                                      </Typography>
                                      <Typography variant="body2">
                                        Low: ₹{data.low.toFixed(2)}
                                      </Typography>
                                      <Typography variant="body2">
                                        Close: ₹{data.close.toFixed(2)}
                                      </Typography>
                                      <Typography variant="body2">
                                        Volume: {data.volume.toLocaleString()}
                                      </Typography>
                                    </Box>
                                  );
                                }
                                return null;
                              }}
                            />
                            {/* Volume bars */}
                            <Bar
                              dataKey="volume"
                              fill="#8884d8"
                              name="Volume"
                              opacity={0.3}
                              yAxisId="volume"
                            />
                            {/* Price lines */}
                            <Line
                              type="monotone"
                              dataKey="high"
                              stroke="#82ca9d"
                              name="High"
                              dot={false}
                              strokeWidth={1}
                            />
                            <Line
                              type="monotone"
                              dataKey="low"
                              stroke="#ff8042"
                              name="Low"
                              dot={false}
                              strokeWidth={1}
                            />
                            {/* Candlestick bodies */}
                            {stockDetails?.historicData?.map((entry, index) => {
                              const isBullish =
                                parseFloat(entry.close) >=
                                parseFloat(entry.open);
                              return (
                                <ReferenceLine
                                  key={index}
                                  x={new Date(entry.date).getTime()}
                                  segment={[
                                    {
                                      x: new Date(entry.date).getTime(),
                                      y: parseFloat(entry.open),
                                    },
                                    {
                                      x: new Date(entry.date).getTime(),
                                      y: parseFloat(entry.close),
                                    },
                                  ]}
                                  stroke={isBullish ? "#82ca9d" : "#ff8042"}
                                  strokeWidth={8}
                                  label=""
                                />
                              );
                            })}
                            {/* Add a second Y-axis for volume */}
                            <YAxis
                              yAxisId="volume"
                              orientation="right"
                              domain={["auto", "auto"]}
                              tickFormatter={(value) =>
                                `${(value / 1000).toFixed(0)}K`
                              }
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  )}

                  {activeTab === 1 && renderOrderSection()}
                  {activeTab === 2 && renderCommentsSection()}
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function PrimaryAlerts() {
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("last_occurrence");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(500);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const fetchData = async () => {
    try {
      console.log("Fetching data with params:", {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        sort_by: orderBy,
        sort_order: order,
      });

      const response = await TF.getSmartAlerts({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        sort_by: orderBy,
        sort_order: order,
      });

      console.log("Received data:", response);
      setData(response.data);
      setTotalCount(response.pagination.total);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    console.log("Effect triggered with:", {
      page,
      rowsPerPage,
      order,
      orderBy,
    });
    fetchData();
  }, [page, rowsPerPage, order, orderBy]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    console.log("Changing page to:", newPage);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    console.log("Changing rows per page to:", newRowsPerPage);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const headCells = [
    { id: "trading_symbol", label: "Symbol" },
    { id: "trigger_count", label: "Trigger Count" },
    { id: "first_occurrence", label: "First Occurrence" },
    { id: "last_occurrence", label: "Last Occurrence" },
    { id: "first_price", label: "First Price" },
    { id: "last_price", label: "Last Price" },
    { id: "price_increase_percentage", label: "Price Increase %" },
    { id: "holdings_quantity", label: "Holdings Qty" },
    { id: "holdings_avg_price", label: "Avg Price" },
  ];

  return (
    <Paper sx={{ width: "100%", mb: 2 }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              {headCells.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  sortDirection={orderBy === headCell.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : "asc"}
                    onClick={() => handleRequestSort(headCell.id)}
                  >
                    {headCell.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <Row key={row.trading_symbol} row={row} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 500, 1000]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        showFirstButton
        showLastButton
        labelRowsPerPage="Rows per page:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} of ${count}`
        }
      />
    </Paper>
  );
}
